import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { createCellCssClassRules, CreateCellDoubleClickHandler, CustomCellDoubleClickAction, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { useKSK2090_R01Store } from "./Store_KSK2090_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { defaultKSK2090_R01DetailDto, KSK2090_R01DetailDto } from "./Dto_KSK2090_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCodeSearch } from "src/hooks/useCodeSearch";
import { createModuleConfig } from "src/utils/detail-table/detailTableUtils";
import { useMouseContent } from "src/utils/mouse/mouseContent";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { headerNames_KSK2090_R01Detail } from "./Util_KSK2090_R01";
import { useCoordinateModal } from "./hooks/useCoordinateModal";
import { useCoordinateCellChange } from "./hooks/useCoordinateCellChange";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";

const COLUMN_STATE_KEY = 'KSK2090_R01-detail-grid-column-state';

const GRID_CELL_CONFIG: GridCellConfig = {
	disabledCells: ['childItemnm', 'coordinateNm', 'coordinateUsage', 'xcoordinate', 'ycoordinate', 'orient', 'topBottom', 'childItemcd', ],
	insertedDisabledCells: [''],
	commonDisabledCells: ['coordinateCd', ],
	
	pointerCells: ['coordinateSeq', 'afeederCd', 'bfeederCd', 'cfeederCd', 'remark', ],
	insertedPointerCells: ['coordinateCd',],
	commonPointerCells: [''],
	alignLeftCells: [''],
};

interface Props {
    gridRef: React.RefObject<AgGridReact<KSK2090_R01DetailDto> | null>;
	defaultKSK2090_R01DetailRow: KSK2090_R01DetailDto;
}

export const DetailTable_KSK2090_R01 = (props: Props) => {

    // [수정] props에서 defaultKSK2090_R01DetailRow를 구조 분해 할당으로 가져옵니다.
    const { gridRef, defaultKSK2090_R01DetailRow } = props;

    const { 
            KSK2090_R01DetailList, setKSK2090_R01DetailList, 
            editKSK2090_R01Detail, setEditKSK2090_R01Detail,
            selectedKSK2090_R01DetailList, setSelectedKSK2090_R01DetailList, removeSelectedKSK2090_R01DetailList, addSelectedKSK2090_R01DetailList, initSelectedKSK2090_R01DetailList,
            deletedKSK2090_R01DetailList, setDeletedKSK2090_R01DetailList, addDeletedKSK2090_R01DetailList, removeDeletedKSK2090_R01DetailList,
            insertKSK2090_R01DetailList, updateKSK2090_R01DetailList, removeKSK2090_R01DetailList,
            detailColumnState, setDetailColumnState,
			originalKSK2090_R01DetailList, setOriginalKSK2090_R01DetailList,
        } = useKSK2090_R01Store();
    const { setCodeSearchModalProps } = useUiuxStore();
    const [clickable, setClickable] = useState(true);
    const { layerList } = useCommonCodeStore();

    const gridWrapperRef = useRef<HTMLDivElement>(null);

    const {
        getRowId,
        createRowClassRules,
        createCellClassRules,
        paddedGridData,
        renderIndexCell,
    } = useGridSettingContent({
        gridRef,
        key: 'valueIndex',
        gridData: KSK2090_R01DetailList,
        defaultGridData: defaultKSK2090_R01DetailDto,
        selectedRow: selectedKSK2090_R01DetailList,
        originalGridData: originalKSK2090_R01DetailList,
        updateRow: (index: number, data: any) => updateKSK2090_R01DetailList(index, data),
    });

    /**
     * 편집 가능한 컬럼 목록 정의
     * 사용자가 직접 입력/수정할 수 있는 필드들을 지정
     */
    const defaultEditableColumns = ['coordinateSeq', 'afeederCd', 'bfeederCd', 'cfeederCd', 'remark'];

    /**
     * 기본 상세 테이블 이벤트 핸들러 훅
     * 
     * 주요 기능:
     * - 키다운 네비게이션 (방향키 이동)
     * - 셀 클릭/키다운 이벤트 처리
     * 
     * @param detailList - 상세 데이터 목록
     * @param editableColumns - 편집 가능한 컬럼 목록
     */
    const cellEventHandlers = useDetailTableEvents({
        detailList: KSK2090_R01DetailList,
        editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
    });


	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = createCellCssClassRules(GRID_CELL_CONFIG);
	
	const gridDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<KSK2090_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedKSK2090_R01DetailList,
            deletedList: deletedKSK2090_R01DetailList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedKSK2090_R01DetailList, deletedKSK2090_R01DetailList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
    ]);

    const baseColumnDefs: ColDef<KSK2090_R01DetailDto>[] = useMemo(() => [
        ...getDefaultRowIndexColumnConfig(
            // (params: KSK2090_R01DetailDto) => renderIndexCell(
            //     params, 
            //     (innerParams: KSK2090_R01DetailDto) => getRowStyleFlags(
            //         innerParams,
            //         {
            //             deletedList: deletedKSK2090_R01DetailList,
            //             selectedList: selectedKSK2090_R01DetailList,
            //             dataList: KSK2090_R01DetailList
            //         }),
            //     (rowIndex: number, e: React.MouseEvent) => InsertRowHandler({
            //         event: e,
            //         dataList: KSK2090_R01DetailList,
            //         rowData: KSK2090_R01DetailList[rowIndex],
            //         insertRow: insertKSK2090_R01DetailList,
            //         defaultRowData: defaultKSK2090_R01DetailRow,
            //         setClickable
            //     }),
            //     (rowData) => RemoveRowHandler( 
            //         gridRef,
            //         KSK2090_R01DetailList, 
            //         rowData,
            //         setClickable, 
            //         removeKSK2090_R01DetailList, 
            //         (valueIndex) => addDeletedKSK2090_R01DetailList([valueIndex.toString()])
            //     ),
            // )
        ),
        {
            headerName: '로케이션', colId: 'coordinateCd', field: 'coordinateCd', width: 90,
            editable: false,
        },
        {
            headerName: '좌표명', colId: 'coordinateNm', field: 'coordinateNm', width: 135,
            editable: false,
        },
		{
            headerName: '좌표순번', colId: 'coordinateSeq', field: 'coordinateSeq', width: 90,
            editable: true,
        },
        {
            headerName: 'X좌표', colId: 'xcoordinate', field: 'xcoordinate', width: 80,
            editable: false,
        },
        {
            headerName: 'Y좌표', colId: 'ycoordinate', field: 'ycoordinate', width: 80,
            editable: false,
        },
        {
            headerName: '방향', colId: 'orient', field: 'orient', width: 60,
            editable: false,
        },
        {
            headerName: 'Layer', colId: 'topBottom', field: 'topBottom', width: 77,
            ...CommonCodeColumn.options(layerList, false, undefined, undefined),
        },
        {
            headerName: '자품목코드', colId: 'childItemcd', field: 'childItemcd', width: 120,
            editable: false,
			cellRenderer: (params: any) => {
                if (!params.value && params.node.rowPinned !== 'bottom') {
                    // 값이 비어있으면 회색 기울임꼴 텍스트로 안내
                    return <span className="text-gray-400 text-[14px]">더블클릭 검색</span>;
                }
                return params.value;
            }
        },
        {
            headerName: '자품목명', colId: 'childItemnm', field: 'childItemnm', width: 120,
            editable: false,
        },
        {
            headerName: '소요량', colId: 'coordinateUsage', field: 'coordinateUsage', width: 90,
            editable: false,
        },
        {
            headerName: 'A피더코드', colId: 'afeederCd', field: 'afeederCd', width: 120,
            editable: true,
        },
        {
            headerName: 'B피더코드', colId: 'bfeederCd', field: 'bfeederCd', width: 120,
            editable: true,
        },
        {
            headerName: 'C피더코드', colId: 'cfeederCd', field: 'cfeederCd', width: 120,
            editable: true,
        },
        {
            headerName: '비고', colId: 'remark', field: 'remark', width: 180,
            editable: true,
        },
		// ...IndexParameterCell(),
    ], [deletedKSK2090_R01DetailList, KSK2090_R01DetailList]);

    const columnDefs: ColDef<KSK2090_R01DetailDto>[] = useMemo(() => {
        if (detailColumnState === null) {
            return baseColumnDefs;
        }
        
        return GridCustomColumnDefs<KSK2090_R01DetailDto>({
            baseColumnDefs,
            columnState: detailColumnState,
            headerNames: headerNames_KSK2090_R01Detail,
            fallbackHeaderClass: aggridHeaderClass
        });
    }, [baseColumnDefs, detailColumnState, headerNames_KSK2090_R01Detail]);

	const { settingCodeSearchModalProps, handlePopupClose } = useCoordinateModal({
		updateKSK2090_R01Detail: updateKSK2090_R01DetailList,
		gridRef,
		KSK2090_R01DetailList,
	});

	const { handleCellChange, handleChildItemCodeSearch } = useCoordinateCellChange({
		KSK2090_R01DetailList,
		updateKSK2090_R01DetailList,
		originalKSK2090_R01DetailList,
		settingCodeSearchModalProps,
		gridRef,
	});

    const handleRowClick = useRowSelectionHandler({
        dataList: KSK2090_R01DetailList,
        idKey: 'valueIndex',
        selectedList: selectedKSK2090_R01DetailList,
        deletedList: deletedKSK2090_R01DetailList,
        addSelected: addSelectedKSK2090_R01DetailList,
        removeSelected: removeSelectedKSK2090_R01DetailList,
        clickable, setClickable
    });

	
	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<KSK2090_R01DetailDto>[] = useMemo(() => [
		{
			validate: (field, data) => (field === 'childItemcd' || field === 'childItemnm'),
			action: (e, foundData) => {
				settingCodeSearchModalProps(
					'BOM',
					foundData.prntItemcd || '',
					false,
					'childItemcd',
					foundData,
					foundData.valueIndex!
				);
			}
		},
		{
			validate: (field, data) => (field === 'afeederCd' || field === 'afeederNm'),
			action: (e, foundData) => {
				settingCodeSearchModalProps(
					'Afeeder',
					foundData.afeederCd || '',
					false,
					'afeederCd',
					foundData,
					foundData.valueIndex!
				);
			}
		},
		{
			validate: (field, data) => (field === 'bfeederCd' || field === 'bfeederNm'),
			action: (e, foundData) => {
				settingCodeSearchModalProps(
					'Bfeeder',
					foundData.bfeederCd || '',
					false,
					'bfeederCd',
					foundData,
					foundData.valueIndex!
				);
			}
		},
		{
			validate: (field, data) => (field === 'cfeederCd' || field === 'cfeederNm'),
			action: (e, foundData) => {
				settingCodeSearchModalProps(
					'Cfeeder',
					foundData.cfeederCd || '',
					false,
					'cfeederCd',
					foundData,
					foundData.valueIndex!
				);
			}
		},
	], [settingCodeSearchModalProps]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(KSK2090_R01DetailList, 'valueIndex', cellDoubleClickActions);


    const handlePostSort = useCallback((params: PostSortRowsParams<KSK2090_R01DetailDto>) => {
        postSortHandler(params, KSK2090_R01DetailList, originalKSK2090_R01DetailList, setKSK2090_R01DetailList);
    }, [KSK2090_R01DetailList, originalKSK2090_R01DetailList, ]);

    const pinnedBottomRowData = useMemo(() => {

        return [{
            coordinateCd: KSK2090_R01DetailList.length.toLocaleString() + "건",
        }];      
    }, [KSK2090_R01DetailList, deletedKSK2090_R01DetailList,]);

    return (
        <>
            <div className="w-full h-full flex flex-col gap-[4px]">				
				<GridContextWrapper<KSK2090_R01DetailDto>
					gridWrapperRef={gridWrapperRef}
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					setPaddedGridData={setKSK2090_R01DetailList}
					initSelected={initSelectedKSK2090_R01DetailList}
					defaultRowData={defaultKSK2090_R01DetailDto}
					baseColumnDefs={columnDefs}    
					setColumnState={setDetailColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Excel<KSK2090_R01DetailDto>
						ref={gridRef}
						rowData={paddedGridData}
						getRowId={getRowId}
						columnDefs={columnDefs}
						defaultColDef={gridDefaultColDef}

						// ✅ 새로운 훅 구조와 호환
						disableBuiltinCellSelection={true} // 내장 셀 선택 비활성화 (새로운 훅 사용)
						columnStateKey={COLUMN_STATE_KEY} // 자동 컬럼 상태 관리 (localStorage)
						setColumnState={setDetailColumnState}

						// 행 스타일 규칙 (선택/삭제/수정 상태에 따른 색상)
						rowClassRules={createRowClassRules<KSK2090_R01DetailDto>({
							idKey: 'valueIndex',
							selectedList: selectedKSK2090_R01DetailList,
							deletedList: deletedKSK2090_R01DetailList
						})}

						// 이벤트 핸들러들
						onRowClicked={handleRowClick}                                    // 행 클릭 (선택)
						onCellClicked={cellEventHandlers.handleCellClick}               // 셀 클릭 (편집 시작)
						onCellDoubleClicked={handleCellDoubleClick}                     // 셀 더블클릭 (코드 검색)
						onCellKeyDown={cellEventHandlers.handleCellKeyDown}             // 셀 키다운 (네비게이션)
						onCellValueChanged={handleCellChange}
						pinnedBottomRowData={pinnedBottomRowData}
						
						// 정렬 후 처리
						onPostSortRows={handlePostSort}
					/>
				</GridContextWrapper>
            </div>
        </>
    )
}